import type { StaffCustomerCoverage, StaffTeam } from "@lafayette/schemas";
import { useState } from "react";

import { SelectField, TextField } from "../../ui/fields";
import { isoToUtcInput, utcInputToIso, utcText } from "../format";
import { assignmentTypeHints, assignmentTypeLabels, availabilityReasonLabels } from "../labels";
import { useTeamRuntime } from "../runtime";
import { TeamCommandDialog } from "./TeamCommandDialog";

// The dialogs of the cover (CHT-768; supervisors): who covers a customer, and when a person is not available. Each sends the supported
// command with a reason and the check of what this screen saw. The API validates the rest (an eligible person, a window that is allowed,
// exactly one primary), so a rule broken here is a refusal with a plain message, never a half change.

type Done = Readonly<{
  onDone: (message: string) => void;
  onReload: () => void;
  onClose: () => void;
}>;
type Person = StaffCustomerCoverage["candidates"][number];

// --- unavailability ---------------------------------------------------------------------------------------------------------------

const REASON_OPTIONS = (
  Object.keys(availabilityReasonLabels) as (keyof typeof availabilityReasonLabels)[]
).map((value) => ({ value, label: availabilityReasonLabels[value] }));

export function UnavailabilityDialog({
  member,
  onDone,
  onReload,
  onClose,
}: Done & Readonly<{ member: StaffTeam["members"][number] }>) {
  const { api, now } = useTeamRuntime();
  const start = now().toISOString();
  const [startsAt, setStartsAt] = useState(isoToUtcInput(start));
  const [endsAt, setEndsAt] = useState(
    isoToUtcInput(new Date(now().getTime() + 8 * 3_600_000).toISOString()),
  );
  const [code, setCode] = useState<string>("ON_LEAVE");
  const [errors, setErrors] = useState<{ startsAt?: string; endsAt?: string }>({});

  return (
    <TeamCommandDialog
      title={`Mark ${member.displayName} unavailable`}
      description="While the window is in force, new handoffs and requests are not routed to this person. A handoff that already waits for them moves to the next cover."
      confirmLabel="Mark unavailable"
      onClose={onClose}
      onReload={onReload}
      onDone={() => onDone(`${member.displayName} is marked unavailable.`)}
      build={() => {
        const from = utcInputToIso(startsAt);
        const to = utcInputToIso(endsAt);
        const next: { startsAt?: string; endsAt?: string } = {};
        if (from === null) next.startsAt = "Enter a start date and time (UTC).";
        if (to === null) next.endsAt = "Enter an end date and time (UTC).";
        if (from !== null && to !== null && Date.parse(to) <= Date.parse(from)) {
          next.endsAt = "The end must be after the start.";
        }
        setErrors(next);
        if (from === null || to === null || next.endsAt !== undefined) return { ok: false };
        return {
          ok: true,
          send: (reason, key) =>
            api.recordUnavailability(
              member.staffUserId,
              {
                startsAt: from,
                endsAt: to,
                reasonCode: code as keyof typeof availabilityReasonLabels,
                reason,
                expectedOpenWindowIds: member.unavailability.map((window) => window.windowId),
              },
              { idempotencyKey: key },
            ),
        };
      }}
    >
      <SelectField
        id="team-window-code"
        label="Why"
        value={code}
        onChange={setCode}
        options={REASON_OPTIONS}
        required
      />
      <TextField
        id="team-window-start"
        label="From (UTC)"
        type="datetime-local"
        value={startsAt}
        error={errors.startsAt}
        required
        onChange={setStartsAt}
      />
      <TextField
        id="team-window-end"
        label="Until (UTC)"
        type="datetime-local"
        value={endsAt}
        error={errors.endsAt}
        hint="A window is at most 90 days. For a longer absence change the cover."
        required
        onChange={setEndsAt}
      />
    </TeamCommandDialog>
  );
}

export function EndUnavailabilityDialog({
  member,
  window,
  onDone,
  onReload,
  onClose,
}: Done &
  Readonly<{
    member: Readonly<{ displayName: string }>;
    window: Readonly<{
      windowId: string;
      reasonCode: keyof typeof availabilityReasonLabels;
      startsAt: string;
      endsAt: string;
    }>;
  }>) {
  const { api } = useTeamRuntime();
  return (
    <TeamCommandDialog
      title={`End the window of ${member.displayName}`}
      description={`${availabilityReasonLabels[window.reasonCode]}, ${utcText(window.startsAt)} to ${utcText(window.endsAt)}. The person can be routed work again at once.`}
      confirmLabel="End window"
      onClose={onClose}
      onReload={onReload}
      onDone={() => onDone(`The window of ${member.displayName} ended.`)}
      build={() => ({
        ok: true,
        send: (reason, key) =>
          api.endUnavailability(window.windowId, { reason }, { idempotencyKey: key }),
      })}
    />
  );
}

// --- the cover of one customer --------------------------------------------------------------------------------------------------

type CoverProps = Done &
  Readonly<{
    customer: StaffCustomerCoverage["customer"];
    /** The ids of the active assignments this screen showed: a stale screen is refused. */
    seen: readonly string[];
    candidates: readonly Person[];
    /** People who cannot be picked for this slot (they hold a slot that the command would refuse). */
    taken: ReadonlySet<string>;
    /** People who hold another slot now (backup or temporary), with its name: shown beside the name, so a promotion is a clear choice. */
    holding?: ReadonlyMap<string, string>;
  }>;

function PersonSelect({
  id,
  value,
  onChange,
  candidates,
  taken,
  holding,
  error,
}: Readonly<{
  id: string;
  value: string;
  onChange: (value: string) => void;
  candidates: readonly Person[];
  taken: ReadonlySet<string>;
  holding?: ReadonlyMap<string, string> | undefined;
  error: string | undefined;
}>) {
  const free = candidates.filter((person) => !taken.has(person.staffUserId));
  return (
    <SelectField
      id={id}
      label="Concierge"
      value={value}
      placeholder="Choose a person"
      required
      error={error}
      onChange={onChange}
      options={free.map((person) => ({
        value: person.staffUserId,
        label:
          holding?.has(person.staffUserId) === true
            ? `${person.displayName} (now ${holding.get(person.staffUserId)})`
            : person.displayName,
      }))}
      hint={free.length === 0 ? "Nobody else with the concierge role is free to pick." : undefined}
    />
  );
}

export function AssignPrimaryDialog({
  customer,
  seen,
  candidates,
  taken,
  holding,
  onDone,
  onReload,
  onClose,
}: CoverProps) {
  const { api } = useTeamRuntime();
  const [person, setPerson] = useState("");
  const [error, setError] = useState<string | undefined>();
  return (
    <TeamCommandDialog
      title={`Assign a primary concierge to ${customer.displayName}`}
      description={assignmentTypeHints.PRIMARY}
      confirmLabel="Assign primary"
      onClose={onClose}
      onReload={onReload}
      onDone={() => onDone(`A primary concierge was assigned to ${customer.displayName}.`)}
      build={() => {
        if (person === "") {
          setError("Choose a person.");
          return { ok: false };
        }
        setError(undefined);
        return {
          ok: true,
          send: (reason, key) =>
            api.assignPrimary(
              customer.id,
              { staffUserId: person, reason, expectedActiveAssignmentIds: [...seen] },
              { idempotencyKey: key },
            ),
        };
      }}
    >
      <PersonSelect
        id="team-person"
        value={person}
        onChange={setPerson}
        candidates={candidates}
        taken={taken}
        holding={holding}
        error={error}
      />
    </TeamCommandDialog>
  );
}

export function ReplacePrimaryDialog({
  customer,
  seen,
  candidates,
  taken,
  holding,
  onDone,
  onReload,
  onClose,
}: CoverProps) {
  const { api } = useTeamRuntime();
  const [person, setPerson] = useState("");
  const [keep, setKeep] = useState<"ENDED" | "BACKUP">("ENDED");
  const [error, setError] = useState<string | undefined>();
  return (
    <TeamCommandDialog
      title={`Replace the primary concierge of ${customer.displayName}`}
      description="The new primary starts at once. The old primary ends, or becomes the backup. There is always exactly one primary."
      confirmLabel="Replace primary"
      onClose={onClose}
      onReload={onReload}
      onDone={() => onDone(`The primary concierge of ${customer.displayName} was replaced.`)}
      build={() => {
        if (person === "") {
          setError("Choose a person.");
          return { ok: false };
        }
        setError(undefined);
        return {
          ok: true,
          send: (reason, key) =>
            api.reassignPrimary(
              customer.id,
              {
                staffUserId: person,
                reason,
                previousPrimaryBecomes: keep,
                expectedActiveAssignmentIds: [...seen],
              },
              { idempotencyKey: key },
            ),
        };
      }}
    >
      <PersonSelect
        id="team-person"
        value={person}
        onChange={setPerson}
        candidates={candidates}
        taken={taken}
        holding={holding}
        error={error}
      />
      <SelectField
        id="team-keep"
        label="The old primary"
        value={keep}
        onChange={(value) => setKeep(value as "ENDED" | "BACKUP")}
        options={[
          { value: "ENDED", label: "Ends" },
          { value: "BACKUP", label: "Becomes the backup (replaces any other backup)" },
        ]}
      />
    </TeamCommandDialog>
  );
}

export function AddBackupDialog({
  customer,
  seen,
  candidates,
  taken,
  holding,
  onDone,
  onReload,
  onClose,
}: CoverProps) {
  const { api } = useTeamRuntime();
  const [person, setPerson] = useState("");
  const [error, setError] = useState<string | undefined>();
  return (
    <TeamCommandDialog
      title={`Add a backup for ${customer.displayName}`}
      description={assignmentTypeHints.BACKUP}
      confirmLabel="Add backup"
      onClose={onClose}
      onReload={onReload}
      onDone={() => onDone(`A backup was added for ${customer.displayName}.`)}
      build={() => {
        if (person === "") {
          setError("Choose a person.");
          return { ok: false };
        }
        setError(undefined);
        return {
          ok: true,
          send: (reason, key) =>
            api.assignBackup(
              customer.id,
              { staffUserId: person, reason, expectedActiveAssignmentIds: [...seen] },
              { idempotencyKey: key },
            ),
        };
      }}
    >
      <PersonSelect
        id="team-person"
        value={person}
        onChange={setPerson}
        candidates={candidates}
        taken={taken}
        holding={holding}
        error={error}
      />
    </TeamCommandDialog>
  );
}

export function AddTemporaryDialog({
  customer,
  seen,
  candidates,
  taken,
  holding,
  onDone,
  onReload,
  onClose,
}: CoverProps) {
  const { api, now } = useTeamRuntime();
  const [person, setPerson] = useState("");
  const [startsAt, setStartsAt] = useState(isoToUtcInput(now().toISOString()));
  const [endsAt, setEndsAt] = useState(
    isoToUtcInput(new Date(now().getTime() + 7 * 24 * 3_600_000).toISOString()),
  );
  const [errors, setErrors] = useState<{ person?: string; startsAt?: string; endsAt?: string }>({});
  return (
    <TeamCommandDialog
      title={`Add temporary cover for ${customer.displayName}`}
      description={assignmentTypeHints.TEMPORARY}
      confirmLabel="Add temporary cover"
      onClose={onClose}
      onReload={onReload}
      onDone={() => onDone(`Temporary cover was added for ${customer.displayName}.`)}
      build={() => {
        const from = utcInputToIso(startsAt);
        const to = utcInputToIso(endsAt);
        const next: { person?: string; startsAt?: string; endsAt?: string } = {};
        if (person === "") next.person = "Choose a person.";
        if (from === null) next.startsAt = "Enter a start date and time (UTC).";
        if (to === null) next.endsAt = "Enter an end date and time (UTC).";
        if (from !== null && to !== null && Date.parse(to) <= Date.parse(from)) {
          next.endsAt = "The end must be after the start.";
        }
        setErrors(next);
        if (from === null || to === null || Object.keys(next).length > 0) return { ok: false };
        return {
          ok: true,
          send: (reason, key) =>
            api.addTemporaryCoverage(
              customer.id,
              {
                staffUserId: person,
                reason,
                startsAt: from,
                endsAt: to,
                expectedActiveAssignmentIds: [...seen],
              },
              { idempotencyKey: key },
            ),
        };
      }}
    >
      <PersonSelect
        id="team-person"
        value={person}
        onChange={setPerson}
        candidates={candidates}
        taken={taken}
        holding={holding}
        error={errors.person}
      />
      <TextField
        id="team-temp-start"
        label="From (UTC)"
        type="datetime-local"
        value={startsAt}
        error={errors.startsAt}
        required
        onChange={setStartsAt}
      />
      <TextField
        id="team-temp-end"
        label="Until (UTC)"
        type="datetime-local"
        value={endsAt}
        error={errors.endsAt}
        required
        onChange={setEndsAt}
      />
    </TeamCommandDialog>
  );
}

export function EndAssignmentDialog({
  customer,
  seen,
  assignment,
  onDone,
  onReload,
  onClose,
}: Done &
  Readonly<{
    customer: StaffCustomerCoverage["customer"];
    seen: readonly string[];
    assignment: StaffCustomerCoverage["assignments"][number];
  }>) {
  const { api } = useTeamRuntime();
  const label = assignmentTypeLabels[assignment.type].toLowerCase();
  return (
    <TeamCommandDialog
      title={`End the ${label} cover of ${assignment.staff.displayName}`}
      description={
        assignment.type === "PRIMARY"
          ? `${customer.displayName} will have no primary concierge. Routing falls back to the backup, then to the supervisor queue.`
          : `${assignment.staff.displayName} will no longer cover ${customer.displayName}.`
      }
      confirmLabel="End cover"
      tone="danger"
      onClose={onClose}
      onReload={onReload}
      onDone={() => onDone(`The ${label} cover of ${assignment.staff.displayName} ended.`)}
      build={() => ({
        ok: true,
        send: (reason, key) =>
          api.endAssignment(
            customer.id,
            assignment.assignmentId,
            { reason, expectedActiveAssignmentIds: [...seen] },
            { idempotencyKey: key },
          ),
      })}
    />
  );
}
